/**
 * Custom Events Tracking — form field errors.
 *
 * form_error
 *
 * Split from assets/analytics.js because reaching every form on the site means
 * crossing shadow boundaries, and that machinery outgrew the core file.
 *
 * Two kinds of form exist here:
 *
 *   1. The theme's own. Every inline field error renders as role="alert" (the
 *      shared snippets/input.liquid and textarea.liquid contract), so watching
 *      that one attribute covers server-rendered and client-validated alike.
 *
 *   2. Forms the theme does not own, rendered inside a shadow root. The contact
 *      page is Shopify Forms (<shopify-forms-embed>), and neither
 *      document.querySelectorAll nor a MutationObserver on <body> can see inside
 *      it — which is why no error there was ever reported (QA, 2026-08-30).
 *      sections/cms-page.liquid already reaches into the same host to style it,
 *      and notes there that the shadow content is invisible to an observer.
 *
 * Per the spec's own development note, `path` is deliberately omitted: GA derives
 * the URL itself.
 */
(function () {
  'use strict';

  var W = window;
  /* Refuse a second execution of this file — see assets/analytics.js. */
  if (W.__whittardAnalyticsForms) return;
  W.__whittardAnalyticsForms = true;
  var A = W.WhittardAnalytics;
  if (!A) return;
  var push = A.push;
  var CONFIG = A.config;
  var onDomChange = A.onDomChange;

  /* Shadow hosts mount asynchronously, and attaching a shadow root is not a
     mutation of the light DOM — so there is nothing to observe and it has to be
     polled. Same 250ms × 40 budget sections/cms-page.liquid uses for the same
     host, for the same reason. */
  var POLL_INTERVAL = 250;
  var POLL_ATTEMPTS = 40;

  var seen = new WeakMap();
  var roots = [];
  var pending = false;

  /* An error is only an error once it is on screen. Several messages render
     their text in the markup and are hidden with a class rather than the hidden
     attribute, so an attribute check reported them on every page load. */
  function visible(el) {
    return !!(el && el.getClientRects && el.getClientRects().length);
  }

  function report(el, allowOutsideForm) {
    /* Scoped to forms so cart and search status regions, which also use
       role="alert", are not reported as field errors. Shadow-rendered forms are
       exempt: their message may sit outside the <form> element in their own
       tree, and the host is a form embed by definition. */
    if (!allowOutsideForm && (!el.closest || !el.closest('form'))) return;
    if (!visible(el)) return;
    var message = (el.textContent || '').trim();
    if (!message) return;
    if (seen.get(el) === message) return;
    seen.set(el, message);
    push('form_error', { alert: message });
  }

  /* A message whose text is already in the markup is revealed by toggling a
     class, which is an attribute mutation the root observers below do not watch
     — and must not, since class toggles are constant. Each message therefore
     gets its own tiny observer. Going out of view clears the dedupe entry, so
     submitting twice and getting the same error twice reports it twice. */
  function bindReveal(el, allowOutsideForm) {
    if (el.__alertWatched) return;
    el.__alertWatched = true;
    new MutationObserver(function () {
      if (visible(el)) report(el, allowOutsideForm);
      else seen.delete(el);
    }).observe(el, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  }

  /* Resolves the message a field points at with aria-describedby. Shadow-rendered
     forms mark the field aria-invalid and put the text in a separate element, so
     role="alert" alone is not guaranteed to find it. */
  function describedMessages(root, field) {
    var ids = (field.getAttribute('aria-describedby') || '').trim();
    if (!ids) return [];
    return ids.split(/\s+/).map(function (id) {
      /* A shadow root has getElementById; a plain Document does too. Neither
         accepts an id starting with a digit through querySelector without
         escaping, which is why this uses getElementById at all. */
      return root.getElementById ? root.getElementById(id) : null;
    }).filter(Boolean);
  }

  function sweepRoot(entry) {
    var root = entry.root;
    var shadow = entry.shadow;

    root.querySelectorAll('[role="alert"]').forEach(function (el) {
      bindReveal(el, shadow);
      report(el, shadow);
    });

    if (!shadow) return;
    root.querySelectorAll('[aria-invalid="true"]').forEach(function (field) {
      describedMessages(root, field).forEach(function (el) {
        bindReveal(el, true);
        report(el, true);
      });
    });
  }

  function sweepAll() {
    pending = false;
    roots.forEach(function (entry) {
      try { sweepRoot(entry); } catch (e) { /* one root must not stop the others */ }
    });
  }

  function schedule() {
    if (pending) return;
    pending = true;
    W.requestAnimationFrame(sweepAll);
  }

  function addRoot(root, shadow) {
    if (!root || root.__alertRootWatched) return null;
    root.__alertRootWatched = true;
    var entry = { root: root, shadow: !!shadow };
    roots.push(entry);
    if (shadow) {
      /* Its own observer: a shadow root is a separate tree, so the document
         observer in assets/analytics.js never sees a single mutation inside it.
         aria-invalid is included because that is how a shadow-rendered form
         announces the field, often before any message element exists. */
      new MutationObserver(schedule).observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-invalid', 'class', 'style', 'hidden']
      });
    }
    sweepRoot(entry);
    return entry;
  }

  function findShadowForms() {
    var found = false;
    document.querySelectorAll(CONFIG.shadowFormHosts).forEach(function (host) {
      if (!host.shadowRoot) return;
      addRoot(host.shadowRoot, true);
      found = true;
    });
    return found;
  }

  var polling = false;

  /* Only ever started where a shadow form host is actually on the page. Polling
     unconditionally would put a ten-second timer on every page view site-wide for
     an element almost none of them have — the same trap the Recharge widget hook
     avoids in assets/analytics-subscription.js. */
  function startPolling() {
    if (polling) return;
    polling = true;
    var attempts = 0;
    var timer = W.setInterval(function () {
      attempts++;
      if (findShadowForms() || attempts > POLL_ATTEMPTS) W.clearInterval(timer);
    }, POLL_INTERVAL);
  }

  function hostAwaitingShadow() {
    var hosts = document.querySelectorAll(CONFIG.shadowFormHosts);
    for (var i = 0; i < hosts.length; i++) {
      if (!hosts[i].shadowRoot) return true;
    }
    return false;
  }

  function watchFormErrors() {
    /* The theme's own forms ride the shared document sweep. Held by reference
       rather than by index into `roots`, which shadow roots also join. */
    var docEntry = addRoot(document, false);

    onDomChange(function () {
      if (docEntry) sweepRoot(docEntry);
      /* Catches a host inserted after load; if its shadow root is not attached
         yet, polling takes over, since that attachment is not a DOM mutation. */
      if (!findShadowForms() && hostAwaitingShadow()) startPolling();
    });

    if (findShadowForms()) return;
    if (hostAwaitingShadow()) startPolling();
  }

  var inited = false;

  function init() {
    if (inited) return;
    inited = true;
    try {
      watchFormErrors();
    } catch (e) {
      if (W.console) W.console.warn('[analytics-forms] failed to initialise', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
